import { ensureMatterArtifactPrivacy } from './ensure-matter-artifact-privacy'
import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'

import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kMatterArtifactVersionsBasePath } from '@/matter-artifacts/constants'
import { MatterArtifactVersionViewableUrlResponseSchema, type MatterArtifactVersionViewableUrlResponse } from '@/matter-artifacts/schemas'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

export async function getMatterArtifactVersionViewableUrl(matterArtifactVersionId: string): Promise<MatterArtifactVersionViewableUrlResponse> {
  const transactionId = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error('User not authenticated')
  await ensureMatterArtifactPrivacy(matterArtifactVersionId, token)

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kMatterArtifactVersionsBasePath}/${encodeURIComponent(matterArtifactVersionId)}/viewable-url`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': transactionId,
    },
  })
  const { data, parseError } = await readResponseJson(response)

  if (!response.ok) {
    throw new ApiError(response.status, getErrorDetail(data) || `Failed to create Matter artifact viewable URL: ${response.status}`)
  }

  if (parseError) {
    Sentry.captureException(new Error('Matter artifact viewable URL response JSON parse failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        matterArtifactVersionId,
        parseError: formatUnknownError(parseError),
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact viewable URL response JSON')
  }

  const parsed = MatterArtifactVersionViewableUrlResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(new Error('Matter artifact viewable URL response schema validation failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        matterArtifactVersionId,
        responseKeys: getTopLevelKeys(data),
        issues: parsed.error.issues,
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact viewable URL response shape', parsed.error.issues)
  }

  return parsed.data
}

export async function fetchMatterArtifactMarkdown(matterArtifactVersionId: string): Promise<string> {
  const { viewable_url } = await getMatterArtifactVersionViewableUrl(matterArtifactVersionId)
  const response = await fetch(viewable_url)
  if (!response.ok) throw new Error(`Failed to fetch Matter artifact markdown: ${response.status}`)
  return response.text()
}

function getErrorDetail(data: unknown): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  if (detail === undefined || detail === null) return null
  return JSON.stringify(detail)
}
